export type UiPreset = "ztron" | "maia";

export type UiPresetStyles = {
	pageClassName: string;
	headingClassName: string;
	labelClassName: string;
	inputClassName: string;
	phonePrefixClassName: string;
	phoneInputClassName: string;
	checkboxClassName: string;
	consentPrimaryClassName: string;
	consentSecondaryClassName: string;
	submitButtonBaseClassName: string;
	submitButtonEnabledClassName: string;
	submitButtonDisabledClassName: string;
	errorClassName: string;
	successAccentColor: string;
	requiresTypekit: boolean;
};

export const PRESET_STYLES: Record<UiPreset, UiPresetStyles> = {
	ztron: {
		pageClassName: "bg-black text-white",
		headingClassName: "text-white text-xl text-center mb-8",
		labelClassName: "block text-xs text-gray-400 mb-1.5",
		inputClassName:
			"w-full px-3 py-2.5 text-sm bg-[#1a1a1a] border border-gray-700 rounded text-white placeholder-gray-600 focus:outline-none focus:border-gray-500",
		phonePrefixClassName:
			"flex items-center px-2.5 bg-[#1a1a1a] border border-r-0 border-gray-700 rounded-l",
		phoneInputClassName:
			"flex-1 px-3 py-2.5 text-sm bg-[#1a1a1a] border border-gray-700 rounded-r text-white placeholder-gray-600 focus:outline-none focus:border-gray-500",
		checkboxClassName:
			"mt-0.5 h-3.5 w-3.5 rounded border-gray-700 bg-[#1a1a1a] text-brand-600 focus:ring-brand-500 focus:ring-offset-0 cursor-pointer",
		consentPrimaryClassName:
			"text-[11px] text-gray-400 leading-relaxed cursor-pointer",
		consentSecondaryClassName: "text-gray-500",
		submitButtonBaseClassName:
			"w-full py-3.5 text-white font-semibold rounded text-base transition-colors",
		submitButtonEnabledClassName:
			"bg-[#5eb75e] hover:bg-[#4ea54e] cursor-pointer",
		submitButtonDisabledClassName: "bg-[#4a9a4a] cursor-not-allowed",
		errorClassName: "mt-2 text-red-500 text-sm text-center animate-fadeOut",
		successAccentColor: "#5eb75e",
		requiresTypekit: false,
	},
	maia: {
		pageClassName: "bg-[#e0e1b7] text-[#17372e]",
		headingClassName:
			'text-[#17372e] text-xl text-center mb-8 font-["Gotham_Narrow","Helvetica_Neue",Arial,sans-serif] uppercase tracking-[0.08em]',
		labelClassName:
			'block text-[16px] font-bold text-[#17372e] mb-2 uppercase tracking-[0.12em] font-["Gotham_Narrow","Helvetica_Neue",Arial,sans-serif]',
		inputClassName:
			"w-full px-3 py-2.5 text-sm bg-[#e0e1b7] border border-[#17372e] rounded-none text-[#17372e] placeholder-[#6f7f76] focus:outline-none focus:border-[#285337] focus:bg-[#e4e5c2] focus:ring-0 focus:ring-offset-0 focus:shadow-none",
		phonePrefixClassName:
			"flex items-center px-2.5 bg-[#e0e1b7] border border-r-0 border-[#17372e] rounded-none",
		phoneInputClassName:
			"flex-1 px-3 py-2.5 text-sm bg-[#e0e1b7] border border-[#17372e] rounded-none text-[#17372e] placeholder-[#6f7f76] focus:outline-none focus:border-[#285337] focus:bg-[#e4e5c2] focus:ring-0 focus:ring-offset-0 focus:shadow-none",
		checkboxClassName:
			"mt-0.5 h-3.5 w-3.5 rounded-none border-[#17372e] bg-[#fcfcfa] text-[#285337] focus:ring-[#c8a738] focus:ring-offset-0 cursor-pointer",
		consentPrimaryClassName:
			"text-[11px] text-[#17372e] leading-relaxed cursor-pointer",
		consentSecondaryClassName: "text-[#4c665b]",
		submitButtonBaseClassName:
			'w-full py-3.5 text-[#17372e] font-bold rounded-full text-sm tracking-[0.14em] uppercase transition-colors font-["Gotham_Narrow","Helvetica_Neue",Arial,sans-serif]',
		submitButtonEnabledClassName:
			"bg-[#c8a738] hover:bg-[#d2b55b] cursor-pointer",
		submitButtonDisabledClassName: "bg-[#9f8e57] cursor-not-allowed",
		errorClassName: "mt-2 text-[#9c2f1f] text-sm text-center animate-fadeOut",
		successAccentColor: "#c8a738",
		requiresTypekit: true,
	},
};

const isUiPreset = (value: string | null | undefined): value is UiPreset => {
	return value === "ztron" || value === "maia";
};

export const resolveUiPreset = (): UiPreset => {
	const presetFromUrl = new URLSearchParams(window.location.search).get(
		"preset",
	);
	if (isUiPreset(presetFromUrl)) {
		return presetFromUrl;
	}

	const presetFromEnv = import.meta.env.VITE_UI_PRESET;
	if (isUiPreset(presetFromEnv)) {
		return presetFromEnv;
	}

	return "maia";
};
